/**
 * RecordingStatusStrip — persistent header indicator while a session is being recorded.
 * Shows a pulsing dot, elapsed time, a live audio-level meter and chunk upload counters,
 * so the therapist can verify capture is working from any tab.
 */
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecordingStatus } from "../features/session/lib/recordingStatus";

const LevelMeter: React.FC<{ stream: MediaStream | null }> = ({ stream }) => {
  const meterRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!stream) return;
    let ctx: AudioContext | null = null;
    let raf = 0;
    try {
      ctx = new AudioContext();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        const level = Math.min(1, Math.sqrt(sum / data.length) * 4);
        meterRef.current?.style.setProperty("--rec-level", level.toFixed(3));
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    } catch (e) {
      console.error("[RecordingStatusStrip] level meter unavailable:", e);
    }
    return () => {
      cancelAnimationFrame(raf);
      ctx?.close().catch((e) => console.error("[RecordingStatusStrip] AudioContext close failed:", e));
    };
  }, [stream]);

  return (
    <span className="recStrip__meter" ref={meterRef} aria-hidden>
      <span />
      <span />
      <span />
      <span />
    </span>
  );
};

function formatElapsed(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export const RecordingStatusStrip: React.FC<{ onClick?: () => void }> = ({ onClick }) => {
  const { t } = useTranslation();
  const status = useRecordingStatus();
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    if (!status.recording) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [status.recording]);

  if (!status.recording || !status.startedAt) return null;

  const elapsed = formatElapsed(Math.max(0, Math.floor((now - status.startedAt) / 1000)));

  return (
    <button
      type="button"
      className="recStrip"
      onClick={onClick}
      title={t("session.recStripHint")}
      aria-label={`${t("session.recordingLabel")} ${elapsed}`}
    >
      <span className="recStrip__dot" aria-hidden />
      <span className="recStrip__label">{t("session.recordingLabel")}</span>
      <span className="recStrip__time">{elapsed}</span>
      <LevelMeter stream={status.stream} />
      <span className="recStrip__chunks">
        {t("session.chunksUploaded", { count: status.chunksUploaded })}
      </span>
      {status.chunksLost > 0 && (
        <span className="recStrip__lost">
          {t("session.chunksLost", { count: status.chunksLost })}
        </span>
      )}
    </button>
  );
};

export default RecordingStatusStrip;
